import React, { useState, useEffect, useRef } from 'react';
import { motion, useScroll, useTransform, AnimatePresence } from 'framer-motion';
import {
  ArrowRight, Check, ChevronDown, Download, Menu, X, Target, Lightbulb,
  TrendingUp, Handshake, Cloud, Server, Brain, Code2, Shield, Database,
  Clock, Award, Users, Star, Quote, Sparkles, Zap, Building2, Play,
  ArrowUpRight, CircleDot, Layers, GitBranch, Rocket, Briefcase,
  UserCheck, ThumbsUp, Calendar, FileCheck, BarChart,
} from 'lucide-react';
import { Link } from 'react-router-dom';

// ============ COLOR PALETTE ============
const COLORS = {
  primary: '#001534',
  secondary: '#FFFFFF',
  accent: '#6366F1',
  accentLight: '#818CF8',
  accentDark: '#4F46E5',
  purple: '#A855F7',
  cyan: '#06B6D4',
  white: '#FFFFFF',
  black: '#000000',
  gray50: '#F9FAFB',
  gray100: '#F3F4F6',
  gray200: '#E5E7EB',
  gray300: '#D1D5DB',
  gray400: '#9CA3AF',
  gray500: '#6B7280',
  gray600: '#4B5563',
  gray700: '#374151',
  gray800: '#1F2937',
  gray900: '#111827',
  gradientStart: '#6366F1',
  gradientEnd: '#A855F7',
};

// ============ ANIMATED COUNTER ============
const AnimatedCounter = ({ end, suffix = '', prefix = '', duration = 2 }) => {
  const [count, setCount] = useState(0);
  const ref = useRef(null);
  const [inView, setInView] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setInView(true); },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const increment = end / (duration * 60);
    const timer = setInterval(() => {
      start += increment;
      if (start >= end) { setCount(end); clearInterval(timer); }
      else setCount(Math.floor(start));
    }, 1000 / 60);
    return () => clearInterval(timer);
  }, [inView, end, duration]);

  return <span ref={ref}>{prefix}{count}{suffix}</span>;
};

// ============ HERO ============
const Hero = () => {
  return (
    <section
      className="relative min-h-[90vh] flex items-center overflow-hidden"
      style={{ backgroundColor: COLORS.primary }}
    >
      {/* Background gradients */}
      <div className="absolute inset-0 overflow-hidden">
        <div
          className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full opacity-20 blur-[120px]"
          style={{ backgroundColor: COLORS.accent }}
        />
        <div
          className="absolute bottom-0 right-1/4 w-[500px] h-[500px] rounded-full opacity-20 blur-[120px]"
          style={{ backgroundColor: COLORS.purple }}
        />
        <div
          className="absolute inset-0 opacity-[0.03]"
          style={{
            backgroundImage: `linear-gradient(${COLORS.white} 1px, transparent 1px), linear-gradient(90deg, ${COLORS.white} 1px, transparent 1px)`,
            backgroundSize: '60px 60px',
          }}
        />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-8 w-full">
        <div className="grid lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-[1.05] tracking-wide mb-6">
              <span style={{ color: COLORS.white }}>Direct Hire Recruitment</span>
              <br />
              <span style={{ color: COLORS.white }}>for </span>
              <span
                className="relative inline-block"
                style={{
                  background: `linear-gradient(135deg, ${COLORS.accentLight}, ${COLORS.purple})`,
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent',
                }}
              >
                Full-Time IT Positions
              </span>
            </h1>

            <p className="text-lg md:text-xl leading-relaxed mb-10 max-w-xl" style={{ color: 'rgba(255,255,255,0.7)', wordBreak: 'normal',overflowWrap: 'normal',hyphens: 'none',whieSpace: 'normal' }}>
              Find and hire the right permanent tech talent. Expert recruitment with 90-day replacement guarantee.
            </p>

            <div className="flex flex-wrap gap-4 mb-12">
              <button
                className="group flex items-center gap-2 px-7 py-4 rounded-xl font-semibold text-base transition-all hover:shadow-2xl hover:shadow-indigo-500/40"
                style={{
                  background: `linear-gradient(135deg, ${COLORS.accent}, ${COLORS.purple})`,
                  color: COLORS.white,
                }}
              >
                Start Hiring
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
              <button
                className="flex items-center gap-2 px-7 py-4 rounded-xl font-semibold text-base border transition-all"
                style={{
                  backgroundColor: 'rgba(255,255,255,0.05)',
                  borderColor: 'rgba(255,255,255,0.2)',
                  color: COLORS.white,
                }}
              >
                How It Works
              </button>
            </div>

            {/* Stats */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
              {[
                { value: 90, suffix: '%', label: 'Fill Rate Success' },
                { value: 4, suffix: ' wks', label: 'Time to Placement', prefix: '2-' },
                { value: 1000, suffix: '+', label: 'Direct Hires Placed' },
                { value: 90, suffix: ' day', label: 'Replacement Guarantee' },
              ].map((stat, i) => (
                <div
                  key={i}
                  className="border-l-2 pl-4"
                  style={{ borderColor: COLORS.accent }}
                >
                  <div className="text-3xl font-semibold" style={{ color: COLORS.white }}>
                    <AnimatedCounter end={stat.value} suffix={stat.suffix} prefix={stat.prefix || ''} />
                  </div>
                  <div className="text-xs mt-1" style={{ color: 'rgba(255,255,255,0.6)' }}>{stat.label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Hero Image */}
          <div className="lg:col-span-6 relative">
            <div className="relative rounded-3xl overflow-hidden shadow-2xl">
              <img
                src="https://ik.imagekit.io/7wqj9br0b/KapilTech_Website_Staffing/Staff%20Augementation/directhire.jpg"
                alt="Direct hire team meeting"
                className="w-full h-full object-fit"
                loading="lazy"
              />
              <div
                className="absolute inset-0"
                style={{
                  background: `linear-gradient(135deg, rgba(99, 102, 241, 0.3), rgba(168, 85, 247, 0.2))`,
                }}
              />
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

// ============ WHY CHOOSE DIRECT HIRE ============
const WhyDirectHire = () => {
  const features = [
    {
      icon: TrendingUp,
      title: 'Long-Term Value',
      desc: 'Permanent employees develop deeper product knowledge and contribute to sustainable growth. Lower total cost of ownership compared to contract alternatives.',
      color: COLORS.accent,
    },
    {
      icon: Users,
      title: 'Cultural Fit',
      desc: 'We assess not just technical skills but cultural alignment. Your new hires will integrate seamlessly into your team and share your company values.',
      color: COLORS.purple,
    },
    {
      icon: Target,
      title: 'Reduced Turnover',
      desc: 'Permanent positions attract committed professionals. Replacement guarantee ensures continuity. Save time and money on recruitment cycles.',
      color: COLORS.cyan,
    },
    {
      icon: Award,
      title: 'Quality Assurance',
      desc: '90-day replacement guarantee. If the hire doesn\'t work out, we replace them free of charge. Your satisfaction is guaranteed.',
      color: '#F59E0B',
    },
  ];

  return (
    <section
      className="relative py-16 overflow-hidden"
      style={{ backgroundColor: COLORS.secondary }}
      id="why-direct-hire"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center max-w-7xl mx-auto mb-20"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6" style={{ color: COLORS.primary }}>
            Why Choose Direct Hire Recruitment?
          </h2>
          <p className="text-lg leading-relaxed max-w-3xl mx-auto" style={{ color: COLORS.gray600 }}>
            Build your core permanent team with talented professionals who are committed to long-term success.
          </p>
        </motion.div>

        {/* Bento Grid */}
        <div className="grid md:grid-cols-2 gap-6">
          {features.map((feat, i) => {
            const Icon = feat.icon;
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.1 }}
                whileHover={{ y: -4 }}
                className="group relative p-8 md:p-10 rounded-3xl border transition-all overflow-hidden"
                style={{
                  backgroundColor: COLORS.gray50,
                  borderColor: COLORS.gray200,
                }}
              >
                <div
                  className="absolute -top-20 -right-20 w-60 h-60 rounded-full opacity-10 blur-3xl transition-opacity group-hover:opacity-20"
                  style={{ backgroundColor: feat.color }}
                />

                <div className="relative">
                  <div
                    className="w-14 h-14 rounded-2xl flex items-center justify-center mb-6"
                    style={{
                      background: `linear-gradient(135deg, ${feat.color}30, ${feat.color}10)`,
                      border: `1px solid ${feat.color}40`,
                    }}
                  >
                    <Icon className="w-7 h-7" style={{ color: feat.color }} />
                  </div>

                  <h3 className="text-2xl font-bold mb-4" style={{ color: COLORS.primary }}>
                    {feat.title}
                  </h3>
                  <p className="leading-relaxed" style={{ color: COLORS.gray600 }}>
                    {feat.desc}
                  </p>
                </div>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

// ============ PROCESS TIMELINE ============
const Process = () => {
  const steps = [
    {
      week: 'Week 1',
      title: 'Requirements & Profiling',
      desc: 'Deep-dive consultation to understand role requirements, team needs, culture, and compensation expectations. We create detailed candidate profile.',
      icon: FileCheck,
      color: COLORS.accent,
    },
    {
      week: 'Week 2',
      title: 'Sourcing & Screening',
      desc: 'Active sourcing from our network and passive candidate recruitment. Technical assessments, phone screens, and qualification validation. Only top candidates advance.',
      icon: Users,
      color: COLORS.purple,
    },
    {
      week: 'Week 3-4',
      title: 'Interviews & Selection',
      desc: 'Facilitate technical interviews, behavioral assessments, and team meetings. We guide candidates through your interview process and handle logistics.',
      icon: Handshake,
      color: COLORS.cyan,
    },
    {
      week: 'Offer',
      title: 'Offer & Onboarding',
      desc: 'Negotiate offers, handle counter-offers, and manage acceptance. Post-placement support ensures smooth onboarding and 90-day success.',
      icon: Rocket,
      color: '#F59E0B',
    },
  ];

  return (
    <section
      className="relative py-16 overflow-hidden"
      style={{ backgroundColor: COLORS.secondary }}
      id="process"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-20"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6" style={{ color: COLORS.primary }}>
            Our Direct Hire Recruitment Process
          </h2>
          <p className="text-lg text-center" style={{ color: COLORS.gray600 }}>
            Streamlined 4-step process to find your perfect permanent hire.
          </p>
        </motion.div>
        </div>

        <div className="relative max-w-7xl mx-auto">
          {/* Vertical line */}
          <div
            className="absolute left-8 md:left-1/2 top-0 bottom-0 w-px md:-translate-x-1/2"
            style={{ backgroundColor: COLORS.gray200 }}
          />

          <div className="space-y-12">
            {steps.map((step, i) => {
              const Icon = step.icon;
              const isLeft = i % 2 === 0;
              return (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, y: 40 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.6, delay: i * 0.1 }}
                  className={`relative flex items-center ${isLeft ? 'md:flex-row' : 'md:flex-row-reverse'}`}
                >
                  {/* Center dot */}
                  <div className="absolute left-8 md:left-1/2 md:-translate-x-1/2 z-10">
                    <motion.div
                      whileHover={{ scale: 1.2 }}
                      className="w-16 h-16 rounded-2xl flex items-center justify-center border-1"
                      style={{
                        backgroundColor: COLORS.white,
                        borderColor: step.color,
                        boxShadow: `0 0 30px ${step.color}40`,
                      }}
                    >
                      <Icon className="w-6 h-6" style={{ color: step.color }} />
                    </motion.div>
                  </div>

                  {/* Content */}
                  <div className={`w-full md:w-1/2 ${isLeft ? 'md:pr-20 pl-24 md:pl-0' : 'md:pl-20 pl-24 md:pr-0'}`}>
                    <motion.div
                      whileHover={{ y: -4 }}
                      className="p-8 rounded-2xl border transition-all"
                      style={{
                        backgroundColor: COLORS.gray50,
                        borderColor: COLORS.gray200,
                      }}
                    >
                      <div className="flex items-center gap-3 mb-4">
                        <span
                          className="px-3 py-1 rounded-full text-xs font-bold tracking-wider"
                          style={{ backgroundColor: `${step.color}20`, color: step.color }}
                        >
                          {step.week}
                        </span>
                        <div className="flex-1 h-px" style={{ backgroundColor: COLORS.gray200 }} />
                        <span className="text-xs font-mono" style={{ color: COLORS.gray500 }}>
                          0{i + 1}
                        </span>
                      </div>
                      <h3 className="text-2xl font-bold mb-3" style={{ color: COLORS.primary }}>
                        {step.title}
                      </h3>
                      <p className="leading-relaxed" style={{ color: COLORS.gray600 }}>
                        {step.desc}
                      </p>
                    </motion.div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </div>
    </section>
  );
};

// ============ FAQ ============
const FAQ = () => {
  const [open, setOpen] = useState(0);
  const faqs = [
    {
      q: 'What does the 90-day replacement guarantee include?',
      a: 'If the hired candidate doesn\'t perform to expectations or isn\'t a cultural fit within 90 days, we\'ll replace them with another qualified candidate at no additional cost. This protects your investment and ensures successful long-term hires.',
    },
    {
      q: 'How long does the direct hire process take?',
      a: 'Typically 2-4 weeks from initial requirements to final placement. Timeline depends on role complexity and how quickly your team completes interviews. For senior/specialized roles, 4-8 weeks is common. We expedite where possible while maintaining quality.',
    },
    {
      q: 'What happens if the candidate leaves within 6 months?',
      a: 'The 90-day guarantee covers performance/fit issues. If the candidate leaves voluntarily after 90 days, that\'s typically not covered. However, we offer extended support (6-month guarantee available as premium option) and are always available to help source replacements if needed.',
    },
    {
      q: 'What\'s your direct hire success rate?',
      a: 'We maintain a 90% fill rate success, meaning 90% of positions we take on are successfully filled. Our 1000+ successful direct hire placements demonstrate our expertise. Candidates placed with us have strong retention rates due to our careful matching process.',
    },
    {
      q: 'Can we negotiate salary and benefits?',
      a: 'Yes! We guide your final offer strategy, help with competitive compensation packages, and handle candidate negotiations. We provide market data and salary benchmarks to ensure your offer is competitive yet fair.',
    },
    {
      q: 'Do you help with onboarding and integration?',
      a: 'Absolutely! We provide 90-day onboarding support with regular check-ins, success metrics tracking, and integration guidance. Our team remains available to address any concerns and ensure the hire is set up for long-term success.',
    },
  ];

  return (
    <section className="relative py-16 overflow-hidden" style={{ backgroundColor: COLORS.primary }} id="faq">
      <div className="max-w-4xl mx-auto px-6 lg:px-8">
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6" style={{ color: COLORS.secondary }}>
            Direct Hire FAQs
          </h2>
        </motion.div>

        <div className="space-y-3 bg-white p-5 rounded-3xl border">
          {faqs.map((faq, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.05 }}
              className="rounded-2xl border overflow-hidden transition-all"
              style={{
                backgroundColor: '#ffffff',
                borderColor: open === i ? 'rgba(99, 102, 241, 0.3)' : COLORS.gray200,
              }}
            >
              <button
                onClick={() => setOpen(open === i ? -1 : i)}
                className="w-full flex items-center justify-between gap-4 p-6 text-left"
              >
                <span className="font-semibold text-base md:text-lg" style={{ color: COLORS.primary }}>
                  {faq.q}
                </span>
                <motion.div
                  animate={{ rotate: open === i ? 180 : 0 }}
                  transition={{ duration: 0.3 }}
                  className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0"
                  style={{ backgroundColor: open === i ? COLORS.accent : COLORS.gray200 }}
                >
                  <ChevronDown className="w-4 h-4" style={{ color: open === i ? COLORS.white : COLORS.primary }} />
                </motion.div>
              </button>

              <AnimatePresence>
                {open === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    transition={{ duration: 0.3 }}
                    className="overflow-hidden"
                  >
                    <div className="px-6 pb-6 leading-relaxed text-justify" style={{ color: COLORS.gray600, wordBreak: 'normal', overflowWrap: 'normal', hyphens: 'none', whiteSpace: 'normal' }}>
                      {faq.a}
                    </div>
                  </motion.div>
                )}
              </AnimatePresence>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};

// ============ FINAL CTA ============
const FinalCTA = () => {
  const COLORS = {
    bg: '#F8FAFC',
    heading: '#0F172A',
    muted: '#475569',
    accent: '#6366F1',
    accentLight: '#A5B4FC',
    white: '#FFFFFF',
    border: '#E2E8F0',
  };

  return (
    <section
      className="relative py-24 overflow-hidden"
      style={{ backgroundColor: COLORS.bg }}
    >
      <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2
            className="text-3xl md:text-4xl lg:text-5xl font-semibold leading-tight mb-6"
            style={{ color: COLORS.heading }}
          >
            Build Your Permanent Tech Team Today
          </h2>

          <p
            className="text-base md:text-lg max-w-2xl mx-auto mb-10"
            style={{ color: COLORS.muted }}
          >
            Expert direct hire recruitment with 90-day replacement guarantee. Find full-time talent that drives growth.
          </p>

          <div className="flex flex-wrap items-center justify-center gap-4">
            <Link
              to="/contact"
              onClick={() => window.scrollTo(0, 0)}
              whileHover={{ scale: 1.03, y: -1 }}
              whileTap={{ scale: 0.98 }}
              className="flex items-center gap-2 px-7 py-3.5 rounded-full font-semibold text-base"
              style={{
                backgroundColor: COLORS.accent,
                color: COLORS.white,
              }}
            >
              Start Your Search
              <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

// ============ MAIN APP ============
export default function DirectHire() {
  return (
    <div style={{ backgroundColor: COLORS.secondary, fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Hero />
      <WhyDirectHire />
      <Process />
      <FAQ />
      <FinalCTA />
    </div>
  );
}
